import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!r.ok && r.status !== 429) {
        // 429 still shows a friendly screen; other errors fall through
      }
      const data = await r.json().catch(() => ({}));
      if (r.status === 429) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Too many requests. Please try again later.');
      } else {
        setDone(true);
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="forgot-password-card">
        <CardHeader>
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-2" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
          <CardTitle className="text-2xl">Forgot your password?</CardTitle>
          <CardDescription>Enter your email and we'll send you a reset link (valid for 60 minutes).</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center py-6" data-testid="forgot-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-800 font-medium mb-2">Check your inbox</p>
              <p className="text-sm text-slate-600">If an account exists for <strong>{email}</strong>, a password reset link has been sent. The link expires in 60 minutes.</p>
              <Link to="/login" className="inline-block mt-4">
                <Button variant="outline" className="rounded-full" data-testid="forgot-back-btn">Back to Login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 h-12"
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="forgot-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
              </Button>
              <p className="text-xs text-slate-500 text-center">No email after a few minutes? Check your spam folder, or contact your administrator.</p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
